import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitInquiry } from "@/lib/api";

const INDUSTRIES = [
  "Automobile / Auto Components",
  "Manufacturing / Heavy Engineering",
  "Logistics & Warehousing",
  "Housekeeping / Facility Management",
  "Pharmaceuticals",
  "Food Processing",
  "Textile",
  "Chemicals",
  "Other",
];

const WORKFORCE_SIZES = ["1–10", "10–50", "50–100", "100–250", "250+"];
const SHIFT_MODELS = [
  "12-hour (2-Shift)",
  "8-hour (3-Shift)",
  "Custom / Mixed",
];

export default function InquiryForm() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_person: "",
    designation: "",
    phone: "",
    email: "",
    industry: "",
    workforce_count: "",
    shift_model: "",
    location: "",
    requirements: "",
  });

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = [
      "company_name",
      "contact_person",
      "phone",
      "email",
      "industry",
      "workforce_count",
      "shift_model",
    ];
    for (const k of required) {
      if (!form[k]) {
        toast.error("Please fill all required fields");
        return;
      }
    }
    setSubmitting(true);
    try {
      await submitInquiry(form);
      setSuccess(true);
      toast.success("Inquiry sent. Our team will reach out within 24 hours.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not send inquiry. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="inquiry-success"
        className="rounded-sm border border-[#1E3A8A]/20 bg-[#1E3A8A]/5 p-8 text-center"
      >
        <CheckCircle2 className="h-12 w-12 text-[#1E3A8A] mx-auto" />
        <h3 className="font-display text-2xl font-bold text-[#0F172A] mt-4">
          Inquiry received
        </h3>
        <p className="text-[#475569] mt-2 max-w-md mx-auto">
          Thanks for reaching out. Our business team will contact you within 24
          working hours with a tailored manpower proposal.
        </p>
        <Button
          onClick={() => {
            setSuccess(false);
            setForm({
              company_name: "",
              contact_person: "",
              designation: "",
              phone: "",
              email: "",
              industry: "",
              workforce_count: "",
              shift_model: "",
              location: "",
              requirements: "",
            });
          }}
          className="mt-6 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-sm"
          data-testid="inquiry-new-button"
        >
          Send another inquiry
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="inquiry-form"
      className="grid grid-cols-1 md:grid-cols-2 gap-5"
    >
      <div>
        <Label htmlFor="company_name">Company Name *</Label>
        <Input
          id="company_name"
          data-testid="inquiry-input-company"
          value={form.company_name}
          onChange={update("company_name")}
          className="mt-2"
          required
        />
      </div>
      <div>
        <Label htmlFor="contact_person">Contact Person *</Label>
        <Input
          id="contact_person"
          data-testid="inquiry-input-contact"
          value={form.contact_person}
          onChange={update("contact_person")}
          className="mt-2"
          required
        />
      </div>
      <div>
        <Label htmlFor="designation">Designation</Label>
        <Input
          id="designation"
          data-testid="inquiry-input-designation"
          value={form.designation}
          onChange={update("designation")}
          className="mt-2"
        />
      </div>
      <div>
        <Label htmlFor="phone">Phone *</Label>
        <Input
          id="phone"
          type="tel"
          data-testid="inquiry-input-phone"
          value={form.phone}
          onChange={update("phone")}
          className="mt-2"
          required
        />
      </div>
      <div>
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          data-testid="inquiry-input-email"
          value={form.email}
          onChange={update("email")}
          className="mt-2"
          required
        />
      </div>
      <div>
        <Label>Industry *</Label>
        <Select
          value={form.industry}
          onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}
        >
          <SelectTrigger
            data-testid="inquiry-select-industry"
            className="mt-2"
          >
            <SelectValue placeholder="Select industry" />
          </SelectTrigger>
          <SelectContent>
            {INDUSTRIES.map((x) => (
              <SelectItem key={x} value={x}>
                {x}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Workforce Required *</Label>
        <Select
          value={form.workforce_count}
          onValueChange={(v) =>
            setForm((f) => ({ ...f, workforce_count: v }))
          }
        >
          <SelectTrigger
            data-testid="inquiry-select-workforce"
            className="mt-2"
          >
            <SelectValue placeholder="Select range" />
          </SelectTrigger>
          <SelectContent>
            {WORKFORCE_SIZES.map((x) => (
              <SelectItem key={x} value={x}>
                {x}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Preferred Shift Model *</Label>
        <Select
          value={form.shift_model}
          onValueChange={(v) => setForm((f) => ({ ...f, shift_model: v }))}
        >
          <SelectTrigger
            data-testid="inquiry-select-shift"
            className="mt-2"
          >
            <SelectValue placeholder="Select shift model" />
          </SelectTrigger>
          <SelectContent>
            {SHIFT_MODELS.map((x) => (
              <SelectItem key={x} value={x}>
                {x}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="location">Site / Location</Label>
        <Input
          id="location"
          data-testid="inquiry-input-location"
          value={form.location}
          onChange={update("location")}
          className="mt-2"
          placeholder="City, area, plant address…"
        />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="requirements">Specific Requirements</Label>
        <Textarea
          id="requirements"
          rows={4}
          data-testid="inquiry-input-requirements"
          value={form.requirements}
          onChange={update("requirements")}
          className="mt-2"
          placeholder="Skill levels, deployment timelines, additional notes…"
        />
      </div>
      <div className="md:col-span-2">
        <Button
          type="submit"
          disabled={submitting}
          data-testid="inquiry-submit-button"
          className="w-full sm:w-auto bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-sm px-8 h-12"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Sending…
            </>
          ) : (
            "Request Workforce"
          )}
        </Button>
      </div>
    </form>
  );
}
